/**
 * PayU Hosted Checkout (same contract as the EPC app's services/payments/payu.ts).
 *
 * The backend owns prices, coupon discounts, the PayU hash and the final
 * status: the frontend only creates the order, form-POSTs the returned fields
 * to PayU unchanged, and reads the server-verified status afterwards.
 */
import { baseURL, requestUrl } from "../../Utils/baseUrls";
import { THREE_D_ANALYSIS_PACKAGE_ID } from "../../Utils/Const";

export const PAYMENT_RESULT_ROUTE = "/payment/result";

export enum BillingBasis {
  Monthly = 0,
  Yearly = 1,
  UsageCount = 2,
  OneTimeProject = 3,
}

export type PaymentStatus = "Pending" | "Initiated" | "Success" | "Failed" | "Refunded";
export const isFinalPaymentStatus = (status?: string) => status === "Success" || status === "Failed" || status === "Refunded";

/** POST /api/payments/create-order body (CreatePayUOrderRequest). Supply exactly one of packageId / featureId. */
export interface CreateOrderRequest {
  projectId: string | null;
  packageId: string | null;
  featureId: string | null;
  billingBasis: BillingBasis;
  quantity: number;
  /** Legacy: the server takes the user from the JWT; if sent it must match. Not sent by this app. */
  userId?: string | null;
  /** Page PayU returns the browser to; must be in the backend's PayU:AllowedReturnUrls. Defaults to this app's result page. */
  returnUrl?: string;
  couponCode: string | null;
}

/** This app's PayU return page, e.g. https://testconsumer.pvnxt.com/payment/result (or http://localhost:3002/payment/result in dev) */
export const paymentReturnUrl = () => `${window.location.origin}${PAYMENT_RESULT_ROUTE}`;

export interface PayUOrder {
  transactionId: string;
  paymentUrl: string;
  method: "POST";
  amount: number;
  originalAmount: number;
  discountAmount: number;
  currency: string;
  fields: Record<string, string>;
}

export interface PaymentStatusRes {
  transactionId: string;
  status: PaymentStatus;
  package: string | null;
  featureId: string | null;
  billingBasis: string;
  quantity: number;
  amount: number;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  currency?: string;
  couponCode: string | null;
  payUId: string | null;
  paymentMethod: string | null;
}

/** One row of GET /api/payments/my-entitlements. */
export interface EntitlementTy {
  package_id: string | null;
  feature_id: string | null;
  project_id: string | null;
  billing_basis: BillingBasis;
  total_count: number | null;
  remaining_count: number | null;
  starts_at: string | null;
  expires_at: string | null;
  is_active: boolean;
}

/** What a checkout pays for, so the app can finish that step after PayU sends the user back. */
export type PaymentPurpose = { type: "proposal" | "3danalysis"; projectId: string };

const TXN_KEY = "payuTransactionId";
const RETURN_KEY = "payuReturnPath";
const PURPOSE_KEY = "payuPurpose";
const COMPLETED_KEY = "payuCompleted";

export const getPendingTransactionId = () => sessionStorage.getItem(TXN_KEY);
export const getPaymentReturnPath = () => sessionStorage.getItem(RETURN_KEY);
export const clearPendingPayment = () => { [TXN_KEY, RETURN_KEY, PURPOSE_KEY].forEach(k => sessionStorage.removeItem(k)); };

/** What the checkout started in this tab pays for, if it said. */
export const getPendingPurpose = (): PaymentPurpose | null => {
  try {
    return JSON.parse(sessionStorage.getItem(PURPOSE_KEY) ?? "null");
  } catch {
    return null;
  }
};

/** Called by the result page after a server-confirmed success: hands the purpose to the page we return to. */
export const markPaymentCompleted = (transactionId: string) => {
  const purpose = sessionStorage.getItem(PURPOSE_KEY);
  if (purpose) sessionStorage.setItem(COMPLETED_KEY, JSON.stringify({ transactionId, purpose: JSON.parse(purpose) }));
};

/** The transaction that completed this purpose, once (it is removed when read); null when there is none. */
export const takeCompletedPayment = (purpose: PaymentPurpose): string | null => {
  try {
    const done = JSON.parse(sessionStorage.getItem(COMPLETED_KEY) ?? "null");
    if (done?.purpose?.type !== purpose.type || done?.purpose?.projectId !== purpose.projectId) return null;
    sessionStorage.removeItem(COMPLETED_KEY);
    return done.transactionId ?? null;
  } catch {
    return null;
  }
};

export const getMyEntitlements = async (): Promise<EntitlementTy[]> => {
  const { data } = await baseURL.get(requestUrl.getMyEntitlements);
  return Array.isArray(data) ? data : data?.responseData ?? [];
};

/** Whether the user holds an active entitlement for this package on this project. A UI hint only; the backend enforces access. */
export const hasProjectPackageAccess = async (packageId: string, projectId: string): Promise<boolean> => {
  const entitlements = await getMyEntitlements();
  return entitlements.some(e => e.is_active
    && e.package_id === packageId
    && (!e.project_id || e.project_id === projectId)
    && (e.remaining_count === null || e.remaining_count === undefined || e.remaining_count > 0));
};

export interface FeatureTy {
  featureId: string;
  featureName: string;
  description: string | null;
  activeStatus: boolean;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  perUsePrice: number | null;
}

// "ProjectCreation", "Project Creation" and "project_creation" are the same feature
const normalizeFeatureName = (name?: string | null) => (name ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

/** The active feature with the given featureName, or null when the catalogue has none. */
export const getFeatureByName = async (featureName: string): Promise<FeatureTy | null> => {
  const { data } = await baseURL.get(requestUrl.getFeature);
  const features: FeatureTy[] = Array.isArray(data?.responseData) ? data.responseData : data?.responseData?.data ?? [];
  const wanted = normalizeFeatureName(featureName);
  const feature = features.find(f => f.activeStatus !== false && normalizeFeatureName(f.featureName) === wanted) ?? null;
  if (!feature) {
    console.warn(`Feature "${featureName}" not found or inactive. Catalogue:`, features.map(f => `${f.featureName} (active: ${f.activeStatus})`));
  }
  return feature;
};

/**
 * Total usage credits bought for a feature. Counts every entitlement row, including used-up
 * ones (a fully used row may turn inactive), since the caller compares against all items ever created.
 */
export const getPurchasedCredits = async (featureId: string): Promise<number> => {
  const entitlements = await getMyEntitlements();
  return entitlements
    .filter(e => e.feature_id === featureId)
    .reduce((sum, e) => sum + (e.total_count ?? e.remaining_count ?? 0), 0);
};

/** Whether this project has paid 3D Roof Analysis; false (ask to pay) when it can't be checked. */
export const has3dAnalysisAccess = async (projectId?: string | null): Promise<boolean> => {
  if (!projectId) return false;
  try {
    return await hasProjectPackageAccess(THREE_D_ANALYSIS_PACKAGE_ID, projectId);
  } catch (err) {
    console.log(err);
    return false;
  }
};

export const createPaymentOrder = async (body: CreateOrderRequest): Promise<PayUOrder> => {
  // returnUrl makes PayU come back to this app; if it isn't allowlisted the backend tries the Origin header,
  // then falls back to PayU:FrontendResultUrl (the EPC app)
  const request: CreateOrderRequest = { ...body, returnUrl: body.returnUrl ?? paymentReturnUrl() };
  const { data } = await baseURL.post<PayUOrder>(requestUrl.createPaymentOrder, request);
  return data;
};

/**
 * A checkout started in this tab whose result page never ran (e.g. PayU's return landed on another site).
 * The app sends the user to the result page to confirm it, so the purchase still completes here.
 */
export const hasUnconfirmedPayment = () => !!getPendingTransactionId() && !!getPaymentReturnPath();

/** Latest payment for a project by this user; status "NotStarted" (hasPayment false) when there is none. */
export const getPaymentStatusByProjectId = async (projectId: string): Promise<{ hasPayment: boolean; transactionId?: string; status: string; packageId?: string | null }> => {
  const { data } = await baseURL.get(`${requestUrl.getPaymentStatusByProjectId}/${encodeURIComponent(projectId)}`);
  return data;
};

/**
 * Whether a successful payment of this package exists for the project, checked on the server: the latest
 * payment for the project, or (if a later purchase replaced it) an active entitlement for the package.
 */
export const hasPaidPackageForProject = async (packageId: string, projectId: string): Promise<{ paid: boolean; transactionId?: string }> => {
  const latest = await getPaymentStatusByProjectId(projectId);
  if (latest.hasPayment && latest.status === "Success" && latest.packageId === packageId) {
    return { paid: true, transactionId: latest.transactionId };
  }
  return { paid: await hasProjectPackageAccess(packageId, projectId) };
};

export const getPaymentStatus = async (transactionId: string): Promise<PaymentStatusRes> => {
  const { data } = await baseURL.get<PaymentStatusRes>(`${requestUrl.getPaymentStatus}/${encodeURIComponent(transactionId)}`);
  return data;
};

/** Leaves the app: browser form POST of the signed fields to PayU. */
export const openPayUCheckout = (order: PayUOrder, returnPath: string, purpose?: PaymentPurpose) => {
  sessionStorage.setItem(TXN_KEY, order.transactionId);
  sessionStorage.setItem(RETURN_KEY, returnPath);
  if (purpose) sessionStorage.setItem(PURPOSE_KEY, JSON.stringify(purpose));
  else sessionStorage.removeItem(PURPOSE_KEY);

  const form = document.createElement("form");
  // Always POST: PayU's _payment only accepts a form POST, whatever the response's method says
  form.method = "POST";
  form.action = order.paymentUrl;
  for (const [name, value] of Object.entries(order.fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = String(value);
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
};

// Set synchronously on the first click, so a double click can't start a second order before React re-renders.
// Never reset after the form is submitted (the page is leaving); a back/forward-cache restore reloads the page.
let checkoutSubmitting = false;

/**
 * The only way to start a checkout: one create-order, then one form POST to PayU, from a click handler.
 * Returns false (and does nothing) when a checkout is already being submitted; throws if create-order fails.
 */
export const startPayUCheckout = async (body: CreateOrderRequest, returnPath: string, purpose?: PaymentPurpose): Promise<boolean> => {
  if (checkoutSubmitting) return false;
  checkoutSubmitting = true;
  try {
    const order = await createPaymentOrder(body);
    openPayUCheckout(order, returnPath, purpose);
    return true;
  } catch (error) {
    checkoutSubmitting = false;
    throw error;
  }
};

/** Backend `message` for a failed payment call. */
export const paymentErrorMessage = (err: any): string => {
  const status = err?.response?.status;
  const body = err?.response?.data;
  // ASP.NET validation errors come as ProblemDetails: { title, errors: { field: [msg] } }.
  const message: string | undefined = body?.message ?? (Object.values(body?.errors ?? {}) as string[][])[0]?.[0] ?? body?.title;
  if (status === 404) return message ?? "This item is no longer available.";
  if (status >= 500) return "Payment service is unavailable. Please try again.";
  return message ?? err?.message ?? "Something went wrong.";
};
